import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Boxes, AlertTriangle, ShieldCheck, FileDown } from "lucide-react";

const BOUNDARY_LABEL = {
  s4_core: "S/4 core", successfactors: "SuccessFactors", ecp: "EC Payroll",
  sales_cloud: "Sales Cloud", service_cloud: "Service Cloud", revenue_cloud: "Revenue Cloud",
  data_cloud: "Data Cloud", marketing_cloud: "Marketing Cloud", experience_cloud: "Experience Cloud",
  out_of_scope: "Out of scope",
};
const EFFORT_STYLE = { low: "text-emerald-600", medium: "text-amber-600", high: "text-red-600" };

// Per-engagement SAP fit-gap backlog — turns a diagnostic into a scoped SOW
// starter to hand an SI. Reads the release-versioned overlay for the streams in
// scope for this engagement. Unverified mappings are flagged (advisory only).
export default function ErpLensPanel({ clientId }) {
  const [pkg, setPkg] = useState("sap_s4hana");
  const [mode, setMode] = useState("greenfield");
  const [packages, setPackages] = useState({});
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/erp/engagements/${clientId}/lens?package=${pkg}&mode=${mode}`)
      .then(({ data }) => setData(data)).catch(() => setData({ mappings: [] }));
  }, [clientId, pkg, mode]);
  useEffect(() => { api.get("/admin/erp/packages").then(({ data }) => setPackages(data.packages || {})).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(
        `/admin/erp/engagements/${clientId}/lens/export.pdf?package=${pkg}&mode=${mode}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fit-gap-sow_${pkg}_${clientId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("SOW starter exported");
    } catch (e) { toast.error("Could not export PDF"); }
    finally { setExporting(false); }
  };

  const rows = data?.mappings || [];
  const effortCount = rows.reduce((a, r) => { a[r.reconfig_effort] = (a[r.reconfig_effort] || 0) + 1; return a; }, {});
  const pkgLabel = packages?.[pkg]?.label || "Blueprint";

  return (
    <div className="ek-surface border ek-line rounded-xl p-5" data-testid="erp-lens-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h3 className="ek-heading text-base flex items-center gap-2"><Boxes size={17} /> {pkgLabel} fit-gap backlog (application lens)</h3>
          <p className="text-[12px] ek-text-muted mt-0.5 max-w-xl">
            The engagement's value streams overlaid with the {pkgLabel} blueprint — a scoped starting backlog for the SI.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="erp-lens-package"
                  className="border ek-line rounded-lg px-2.5 py-1.5 text-[12.5px] ek-surface">
            {Object.entries(packages).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="inline-flex rounded-lg border ek-line overflow-hidden text-[12px]">
            {["greenfield", "brownfield"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`erp-lens-mode-${m}`}
                      className={`px-3 py-1.5 capitalize ${mode === m ? "bg-[#7737F6] text-white" : "ek-text-soft hover:bg-zinc-50"}`}>{m}</button>
            ))}
          </div>
          <button onClick={exportPdf} disabled={exporting || !rows.length} data-testid="erp-lens-export-pdf"
                  title={rows.length ? "Export a branded, SI-ready SOW starter" : "No mappings to export"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] border ek-line rounded-lg ek-text-soft hover:bg-zinc-50 disabled:opacity-40">
            <FileDown size={14} /> {exporting ? "Exporting…" : "SOW PDF"}
          </button>
        </div>
      </div>

      {data?.any_unverified && (
        <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11.5px] text-amber-900 flex items-center gap-2 mb-3" data-testid="erp-lens-disclaimer">
          <AlertTriangle size={13} /> {data.disclaimer}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-[12.5px] ek-text-muted py-4" data-testid="erp-lens-empty">
          No {pkgLabel} mappings for this engagement's streams yet. Add mappings in Enterprise360 → Application blueprints.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 text-[11.5px] ek-text-muted mb-3" data-testid="erp-lens-summary">
            <span><strong className="ek-text">{rows.length}</strong> backlog items</span>
            <span>effort: <span className={EFFORT_STYLE.high}>{effortCount.high || 0}H</span> · <span className={EFFORT_STYLE.medium}>{effortCount.medium || 0}M</span> · <span className={EFFORT_STYLE.low}>{effortCount.low || 0}L</span></span>
          </div>
          <div className="border ek-line rounded-lg divide-y divide-zinc-100">
            {rows.map((m) => (
              <div key={m.id} className="p-3" data-testid={`erp-lens-row-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-medium ek-text">{m.title || m.node_id}</span>
                  {m.scope_item_id ? <span className="text-[10px] font-mono bg-[#7737F6]/10 text-[#7737F6] px-1.5 py-0.5 rounded">{m.scope_item_id}</span> : null}
                  <span className="text-[10px] ek-text-muted border ek-line rounded px-1.5 py-0.5">{BOUNDARY_LABEL[m.boundary] || m.boundary}</span>
                  <span className={`text-[10px] font-semibold ${EFFORT_STYLE[m.reconfig_effort]}`}>{m.reconfig_effort} effort</span>
                  {m.sunset ? <span className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">sunset {m.sunset}</span> : null}
                  {m.unverified
                    ? <span className="text-[10px] text-amber-700 flex items-center gap-0.5"><AlertTriangle size={9} /> unverified</span>
                    : <span className="text-[10px] text-emerald-700 flex items-center gap-0.5"><ShieldCheck size={9} /> verified</span>}
                </div>
                <div className="text-[11px] ek-text-muted mt-0.5">{m.stream_id} · {m.node_id} · {m.lob}</div>
                {m.note ? <div className="text-[12px] ek-text-soft mt-1">{m.note}</div> : null}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
